/**
 * UX-F01 (Phase 27 3-F) — generic destructive-confirm modal.
 *
 * Replaces ``window.confirm()`` at two call sites
 * (``ActionBar.tsx`` Reset-section + ``SpecHeader.tsx``
 * Reset-all). The brief identified the prior native confirm
 * as visually jarring AND silently suppressed in
 * embedded/headless Chrome (returning false = button
 * appears to do nothing). The styled modal:
 *
 *   - Renders an in-app dialog using the same fixed-position
 *     + ``bg-black/30`` backdrop as the existing
 *     OverwriteConfirmDialog / ParagraphDeleteConfirm pattern.
 *   - role="dialog" + aria-modal so screen readers handle it
 *     correctly; aria-label carries the title.
 *   - Closes on Cancel without any action; ``onConfirm`` fires
 *     a fresh user-initiated promise so the modal can show a
 *     "...working" spinner via the optional ``isPending`` prop.
 *   - data-testid on the root + buttons for e2e smoke.
 *
 * Pattern matches ParagraphDeleteConfirm.tsx so the spec
 * workbench has one consistent confirm-modal shape.
 */
import React from 'react';
import { Loader2 } from 'lucide-react';

interface Props {
  /** Heading text — usually a question ("Reset section?"). */
  title: string;
  /** Body copy — full sentence explaining what's about to happen
   *  + that the action can't be undone. */
  message: string;
  /** Label for the destructive button. Defaults to "Confirm". */
  confirmLabel?: string;
  /** Label for the cancel button. Defaults to "Cancel". */
  cancelLabel?: string;
  /** True while the action is in flight — disables both buttons
   *  + shows a spinner on Confirm. */
  isPending?: boolean;
  /** Called when the user clicks the destructive button. */
  onConfirm: () => void;
  /** Called when the user clicks Cancel or hits Escape. */
  onCancel: () => void;
  /** Optional data-testid suffix so e2e tests can scope to a
   *  specific confirm (e.g. ``reset-section`` / ``reset-all``).
   *  Defaults to ``confirm-dialog`` if omitted. */
  testIdSuffix?: string;
}

export default function ConfirmDialog({
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  isPending = false,
  onConfirm,
  onCancel,
  testIdSuffix = 'confirm-dialog',
}: Props): JSX.Element {
  // Esc dismisses — UX nicety matching the OS-level confirm we're
  // replacing. We bind once on mount and clean up on unmount; the
  // listener is no-op when the modal isn't visible because React
  // tears the component down with the parent's conditional render.
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isPending) {
        e.preventDefault();
        onCancel();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isPending, onCancel]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      data-testid={testIdSuffix}
      onClick={(e) => {
        // Backdrop click cancels; clicks on the inner panel are
        // stopPropagation'd via the child div's onClick below so
        // the user doesn't dismiss while interacting with the
        // dialog body.
        if (e.target === e.currentTarget && !isPending) {
          onCancel();
        }
      }}
    >
      <div
        className="w-full max-w-md rounded-sm border border-zinc-200 bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="mb-2 text-base font-semibold text-zinc-900">
          {title}
        </h2>
        <p className="mb-6 text-sm leading-relaxed text-zinc-600">
          {message}
        </p>
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            className="inline-flex h-9 items-center rounded-sm border border-zinc-300 bg-white px-3 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
            onClick={onCancel}
            disabled={isPending}
            data-testid={`${testIdSuffix}-cancel`}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className="inline-flex h-9 items-center gap-1 rounded-sm bg-red-600 px-3 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
            onClick={onConfirm}
            disabled={isPending}
            data-testid={`${testIdSuffix}-confirm`}
          >
            {isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
